import { css } from '@emotion/react';
import { ToucanComponents } from '@jointoucan/toucan-design';
import { useMediaQuery, useTheme } from '@mui/material';
import { capitalize } from 'lodash';
import { GetServerSideProps } from 'next';

import { FeaturesGrid } from '~/components/LearnSmarter/FeaturesGrid';
import { Box } from '~/components/shared/Box';
import Layout from '~/components/shared/Layout';
import { SignUpModalButton } from '~/components/shared/SignUpModalButton';
import { Events } from '~/constants/events';
import { Language } from '~/constants/languages';
import { trackEvent } from '~/helpers/analytics';
import { useMount } from '~/hooks/useMount';
import { getTranslations } from '~/lib/next-i18next';

const { Typography, useColorScheme } = ToucanComponents;

const LearnSmarter = () => {
  const theme = useTheme();
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isViewportAboveMd = useMediaQuery(theme.breakpoints.up('md'));
  const { textLevel1, textLevel2 } = useColorScheme();

  const languages = [
    Language.Spanish,
    Language.French,
    Language.German,
    Language.Korean,
    Language.Japanese,
    Language.Chinese,
    Language.Italian,
    Language.Portuguese,
    Language.Arabic,
    Language.Hindi,
    Language.Hebrew,
  ];

  useMount(() => {
    trackEvent({
      name: Events.LearnSmarterPageViewed,
    });
  });

  return (
    <Layout isNavbarVisible hasFooter={false} isNavbarRightContentVisible={false} isContained={false}>
      <Box
        pt={4}
        px={2}
        pb={10}
        display="flex"
        flexDirection="column"
        alignItems="center"
        css={css`
          overflow-x: clip;
          ${theme.breakpoints.up('sm')} {
            padding-left: ${theme.spacing(3)};
            padding-right: ${theme.spacing(3)};
            padding-bottom: ${theme.spacing(16)};
          }
          background: linear-gradient(
              180deg,
              rgba(232, 237, 229, 0) -17.98%,
              rgba(232, 237, 229, 0.43) -2.98%,
              rgba(192, 226, 223, 0.2) 26.18%,
              rgba(207, 236, 245, 0.39) 100%
            ),
            #ffffff;
        `}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="center"
          position="relative"
          css={css`
            ${theme.breakpoints.up('sm')} {
              flex-direction: row;
            }

            position: relative;

            &:before {
              content: '';
              position: absolute;
              background-repeat: no-repeat;
              width: 320px;
              height: 224px;
              ${theme.breakpoints.up('sm')} {
                right: -166px;
                top: -32px;
                background-image: url(/images/new-years-promo/japan-arches.svg);
                background-size: 190px;
                width: 190px;
              }
              ${theme.breakpoints.up('md')} {
                right: -146px;
                background-size: 320px;
                width: 320px;
              }
            }

            &:after {
              content: '';
              position: absolute;
              background-repeat: no-repeat;
              width: 340px;
              ${theme.breakpoints.up('sm')} {
                left: -110px;
                bottom: -297px;
                height: 289px;
                background-image: url(/images/new-years-promo/france-eiffel-tower.svg);
                background-size: 190px;
              }
              ${theme.breakpoints.up(1071)} {
                bottom: -258px;
              }
              ${theme.breakpoints.up('md')} {
                bottom: -204px;
                left: -110px;
                background-size: 340px;
              }
            }
          `}
        >
          <Box position="relative">
            <img
              src="/images/new-years-promo/hero.svg"
              css={css`
                width: 200px;
                margin: 10px;
                ${theme.breakpoints.up('md')} {
                  width: 400px;
                  margin-left: ${theme.spacing(3)};
                  margin-right: ${theme.spacing(3)};
                }
              `}
              alt="hero"
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            css={css`
              & h3,
              & h4,
              p {
                text-align: center;
              }
              ${theme.breakpoints.up('sm')} {
                margin-left: ${theme.spacing(2)};
                margin-right: ${theme.spacing(2)};
                max-width: 368px;
                & h3,
                & h4,
                p {
                  text-align: left;
                }
              }
              ${theme.breakpoints.up('md')} {
                max-width: 646px;
                margin-left: ${theme.spacing(4)};
                margin-right: ${theme.spacing(4)};
              }
            `}
          >
            <Typography variant={isViewportAboveMd ? 'xl' : 'lg'} font="secondary" color={textLevel1}>
              Learn smarter,
              <br /> not harder
            </Typography>
            <Box mt={1}>
              <Typography variant={isViewportAboveMd ? 'md' : 'sm'} color={textLevel2}>
                Learn to speak a new language with confidence just by browsing the internet.
              </Typography>
            </Box>

            <Box
              mt={3}
              display="flex"
              flexDirection="column"
              gap={2}
              css={css`
                ${theme.breakpoints.up('md')} {
                  flex-direction: row;
                  align-items: center;
                }
              `}
            >
              <SignUpModalButton trackingEventName={Events.LearnSmarterPageCTAClicked} buttonLocation="Hero" />
            </Box>
          </Box>
        </Box>
        <Box mt={4} zIndex={5} display="flex" flexWrap="wrap" justifyContent="center" gap={isViewportAboveSm ? 1 : 0}>
          {languages.map((language) => (
            <Box key={language} p={1} display="flex" gap={1} alignItems="center">
              <img src={`/images/flags/round/${language}.svg`} alt={`${language} flag`} width="10.5px" />
              <Typography variant="xs" isBold color={textLevel2}>
                {capitalize(language)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <FeaturesGrid />
      <Box
        pt={(() => {
          if (isViewportAboveMd) {
            return 25;
          }
          return isViewportAboveSm ? 18 : 10;
        })()}
        px={3}
        pb={5}
        css={css`
          background: linear-gradient(
              180deg,
              rgba(232, 237, 229, 0) -17.98%,
              rgba(232, 237, 229, 0.43) -2.98%,
              rgba(192, 226, 223, 0.2) 26.18%,
              rgba(207, 236, 245, 0.39) 100%
            ),
            #ffffff;
        `}
        display="flex"
        justifyContent="center"
      >
        <Box
          display="flex"
          flexDirection="column"
          css={css`
            max-width: 1144px;
            ${theme.breakpoints.up('sm')} {
              flex-direction: row;
              align-items: center;
            }
          `}
        >
          <Box px={isViewportAboveMd ? 4 : 2} flex={1}>
            <Typography variant={isViewportAboveMd ? 'lg' : 'md'} font="secondary" color={textLevel1}>
              How Toucan works
            </Typography>
            <Box mt={2}>
              <Typography variant={isViewportAboveMd ? 'md' : 'sm'} color={textLevel2}>
                Each time you visit a website, Toucan translates key words and phrases on the page into the language
                you’re trying to learn. So you naturally absorb words in your new language by using context clues in a
                language you already know.
              </Typography>
            </Box>
            <Box mt={4}>
              <SignUpModalButton
                trackingEventName={Events.LearnSmarterPageCTAClicked}
                buttonLocation="How Toucan Works"
              />
            </Box>
          </Box>
          <Box mt={2} flex={1}>
            <img
              src="/images/new-years-promo/browser.svg"
              alt="browser"
              css={css`
                width: 100%;
                max-width: 480px;
              `}
            />
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => ({
  props: {
    ...(await getTranslations({ locale })),
    locale,
  },
});

export default LearnSmarter;
